import { useState, useEffect, useMemo, useCallback } from 'react';
import { useUpdateAppealDeadlines } from '../queries';
import { AppealItem } from '../types';
import { AppealStatus } from '@repo/types';

const ALLOWED_EDIT_DEADLINES = new Set([
  AppealStatus.PRE_APPEAL,
  AppealStatus.TO_BE_APPEALED,
  AppealStatus.AWAITING_APPEAL_SUBMISSION,
  AppealStatus.APPEAL_SUBMITTED,
]);

export interface DeadlineState {
  isEditing: boolean;
  current: string | undefined;
  edited: Date | null;
  error: string;
}

const toDateOnlyString = (date: Date | null): string | null => {
  if (!date) return null;
  return (
    new Date(date.getFullYear(), date.getMonth(), date.getDate(), 12).toISOString().split('T')[0] ??
    null
  );
};

const useEditableDeadline = (
  initialValue: string | undefined,
  onSave: (val: string | null) => void,
  validate: (date: Date | null) => string
) => {
  const [state, setState] = useState<DeadlineState>({
    isEditing: false,
    current: initialValue,
    edited: initialValue ? new Date(initialValue) : null,
    error: '',
  });

  useEffect(() => {
    setState(prev => ({
      ...prev,
      current: initialValue,
      edited: initialValue ? new Date(initialValue) : null,
      isEditing: false,
      error: '',
    }));
  }, [initialValue]);

  const setEdited = useCallback(
    (date: Date | null) => {
      const error = validate(date);
      setState(prev => ({ ...prev, edited: date, error }));
    },
    [validate]
  );

  const startEdit = useCallback(() => {
    setState(prev => ({
      ...prev,
      isEditing: true,
      edited: prev.current ? new Date(prev.current) : null,
      error: '',
    }));
  }, []);

  const cancelEdit = useCallback(() => {
    setState(prev => ({
      ...prev,
      isEditing: false,
      edited: prev.current ? new Date(prev.current) : null,
      error: '',
    }));
  }, []);

  const save = useCallback(() => {
    const error = validate(state.edited);
    if (error) {
      setState(prev => ({ ...prev, error }));
      return;
    }

    const editedStr = toDateOnlyString(state.edited);
    // If no change, just exit edit mode
    if ((state.current ?? null) === editedStr) {
      setState(prev => ({ ...prev, isEditing: false }));
      return;
    }

    onSave(editedStr);
  }, [state.edited, state.current, validate, onSave]);

  const clear = useCallback(() => {
    onSave(null);
  }, [onSave]);

  return {
    state,
    actions: {
      setEdited,
      startEdit,
      cancelEdit,
      save,
      clear,
      setError: (error: string) => setState(prev => ({ ...prev, error })),
    },
  };
};

export const useAppealDeadline = (appeal: AppealItem) => {
  const updateDeadlinesMutation = useUpdateAppealDeadlines(appeal.invoiceId, appeal.appealId);
  const isSaving = updateDeadlinesMutation.isPending;

  // Get today's date at start of day for min date validation
  const getTodayDate = useCallback(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return today;
  }, []);

  const mutateDeadline = (val: string | null) => {
    updateDeadlinesMutation.mutate({ deadline: val });
  };
  const mutateAttyDeadline = (val: string | null) => {
    updateDeadlinesMutation.mutate({ attyDeadline: val });
  };

  // Instantiate hooks with dummy validators initially to avoid circular dependency
  // We will override the validation logic in the wrappers below
  const deadline = useEditableDeadline(appeal.deadline, mutateDeadline, () => '');
  const attyDeadline = useEditableDeadline(appeal.attyDeadline, mutateAttyDeadline, () => '');

  // Validation logic that needs access to the other hook's state
  const validateDeadlineDate = useCallback(
    (date: Date | null): string => {
      if (!date) return '';
      const today = getTodayDate();
      const dateOnly = new Date(date.getFullYear(), date.getMonth(), date.getDate());
      if (dateOnly < today) {
        return 'Cannot select a date from the past';
      }

      // Check if client deadline is earlier than existing atty deadline
      let attyDeadlineForValidation: Date | null = null;
      if (attyDeadline.state.isEditing) {
        attyDeadlineForValidation = attyDeadline.state.edited;
      } else if (attyDeadline.state.current) {
        attyDeadlineForValidation = new Date(attyDeadline.state.current);
      }

      if (attyDeadlineForValidation) {
        const attyDeadlineOnly = new Date(
          attyDeadlineForValidation.getFullYear(),
          attyDeadlineForValidation.getMonth(),
          attyDeadlineForValidation.getDate()
        );
        if (dateOnly < attyDeadlineOnly) {
          return 'Client Appeal Deadline cannot be earlier than Atty Response Deadline';
        }
      }
      return '';
    },
    [
      getTodayDate,
      attyDeadline.state.current,
      attyDeadline.state.isEditing,
      attyDeadline.state.edited,
    ]
  );

  const validateAttyDate = useCallback(
    (date: Date | null): string => {
      if (!date) return '';
      const today = getTodayDate();
      const dateOnly = new Date(date.getFullYear(), date.getMonth(), date.getDate());
      if (dateOnly < today) {
        return 'Cannot select a date from the past';
      }

      let clientDeadlineForValidation: Date | null = null;
      if (deadline.state.isEditing) {
        clientDeadlineForValidation = deadline.state.edited;
      } else if (deadline.state.current) {
        clientDeadlineForValidation = new Date(deadline.state.current);
      }

      if (clientDeadlineForValidation) {
        const clientDeadlineOnly = new Date(
          clientDeadlineForValidation.getFullYear(),
          clientDeadlineForValidation.getMonth(),
          clientDeadlineForValidation.getDate()
        );
        if (dateOnly > clientDeadlineOnly) {
          return 'Atty Response Deadline cannot be after Client Appeal Deadline';
        }
      }
      return '';
    },
    [getTodayDate, deadline.state.current, deadline.state.isEditing, deadline.state.edited]
  );

  // Wrappers to inject validation
  const handleDeadlineChange = (date: Date | null) => {
    const error = validateDeadlineDate(date);
    deadline.actions.setEdited(date);
    if (error) deadline.actions.setError(error);

    // Cross-validation: re-validate atty deadline if it's being edited
    if (attyDeadline.state.isEditing && attyDeadline.state.edited) {
      const attyError = validateAttyDate(attyDeadline.state.edited);
      attyDeadline.actions.setError(attyError);
    }
  };

  const handleAttyDeadlineChange = (date: Date | null) => {
    const error = validateAttyDate(date);
    attyDeadline.actions.setEdited(date);
    if (error) attyDeadline.actions.setError(error);
  };

  const saveDeadline = () => {
    const error = validateDeadlineDate(deadline.state.edited);
    if (error) {
      deadline.actions.setError(error);
      return;
    }

    const editedStr = toDateOnlyString(deadline.state.edited);
    if ((deadline.state.current ?? null) === editedStr) {
      deadline.actions.cancelEdit();
      return;
    }
    mutateDeadline(editedStr);
  };

  const saveAttyDeadline = () => {
    const error = validateAttyDate(attyDeadline.state.edited);
    if (error) {
      attyDeadline.actions.setError(error);
      return;
    }
    const editedStr = toDateOnlyString(attyDeadline.state.edited);
    if ((attyDeadline.state.current ?? null) === editedStr) {
      attyDeadline.actions.cancelEdit();
      return;
    }
    mutateAttyDeadline(editedStr);
  };

  const startEditDeadline = () => {
    if (attyDeadline.state.isEditing) attyDeadline.actions.cancelEdit();
    deadline.actions.startEdit();
  };

  const startEditAtty = () => {
    if (deadline.state.isEditing) deadline.actions.cancelEdit();
    attyDeadline.actions.startEdit();
  };

  const canEditDeadlines = ALLOWED_EDIT_DEADLINES.has(appeal.appealStatus as AppealStatus);

  const getClientDeadlineDate = (): Date | null => {
    if (deadline.state.isEditing && deadline.state.edited) {
      return deadline.state.edited;
    }
    return deadline.state.current ? new Date(deadline.state.current) : null;
  };

  return {
    deadline: {
      ...deadline.state,
      ...deadline.actions,
      handleChange: handleDeadlineChange,
      save: saveDeadline,
      startEdit: startEditDeadline,
    },
    attyDeadline: {
      ...attyDeadline.state,
      ...attyDeadline.actions,
      handleChange: handleAttyDeadlineChange,
      save: saveAttyDeadline,
      startEdit: startEditAtty,
    },
    isSaving,
    getTodayDate,
    getClientDeadlineDate,
    canEditDeadlines,
  };
};
